import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Stack,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarMonth as CalendarIcon,
  Notifications as NotificationIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface Holiday {
  id: number;
  date: string;
  name: string;
  country: string;
}

interface Project {
  id: number;
  name: string;
  code: string;
  description: string;
  created_at: string;
}

const Settings: React.FC = () => {
  // State
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Settings state
  const [settings, setSettings] = useState({
    emailNotifications: true,
    deadlineReminders: true,
    reminderDays: 7,
    defaultWorkingDays: 5,
    projectCode: 'IGET',
    organizationName: 'Immingham Green Energy Terminal',
  });

  // Holiday dialog
  const [holidayDialog, setHolidayDialog] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '', country: 'UK' });

  // Project dialog
  const [projectDialog, setProjectDialog] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', code: '', description: '' });

  // Fetch data
  useEffect(() => {
    fetchHolidays();
    fetchProjects();
  }, []);

  const fetchHolidays = async () => {
    try {
      const response = await fetch(`${API_BASE}/consents/holidays`);
      if (response.ok) {
        const data = await response.json();
        setHolidays(data);
      }
    } catch (err) {
      console.error('Error fetching holidays:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API_BASE}/consents/projects`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const handleSaveSettings = () => {
    // In production, save to backend
    localStorage.setItem('assertion_settings', JSON.stringify(settings));
    setSnackbar({ open: true, message: 'Settings saved successfully', severity: 'success' });
  };

  const handleAddHoliday = async () => {
    try {
      const response = await fetch(`${API_BASE}/consents/holidays`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newHoliday),
      });
      
      if (response.ok) {
        fetchHolidays();
        setHolidayDialog(false);
        setNewHoliday({ date: '', name: '', country: 'UK' });
        setSnackbar({ open: true, message: 'Holiday added', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add holiday', severity: 'error' });
    }
  };

  const handleDeleteHoliday = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE}/consents/holidays/${id}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        fetchHolidays();
        setSnackbar({ open: true, message: 'Holiday deleted', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete holiday', severity: 'error' });
    }
  };

  const handleAddProject = async () => {
    try {
      const response = await fetch(`${API_BASE}/consents/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newProject),
      });
      
      if (response.ok) {
        fetchProjects();
        setProjectDialog(false);
        setNewProject({ name: '', code: '', description: '' });
        setSnackbar({ open: true, message: 'Project added', severity: 'success' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add project', severity: 'error' });
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  // Group holidays by year
  const holidaysByYear = holidays.reduce((acc, h) => {
    const year = new Date(h.date).getFullYear();
    if (!acc[year]) acc[year] = [];
    acc[year].push(h);
    return acc;
  }, {} as Record<number, Holiday[]>);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={600}>
          Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure your consent management system
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="General Settings"
              avatar={<BusinessIcon color="primary" />}
            />
            <Divider />
            <CardContent>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  value={settings.organizationName}
                  onChange={(e) => setSettings({ ...settings, organizationName: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Project Code Prefix"
                  value={settings.projectCode}
                  onChange={(e) => setSettings({ ...settings, projectCode: e.target.value })}
                  helperText="Used for generating consent references (e.g., IGET-MAR-01-001)"
                />
                <TextField
                  fullWidth
                  label="Default Working Days Per Week"
                  type="number"
                  value={settings.defaultWorkingDays}
                  onChange={(e) => setSettings({ ...settings, defaultWorkingDays: parseInt(e.target.value) })}
                  inputProps={{ min: 1, max: 7 }}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Notification Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader 
              title="Notifications"
              avatar={<NotificationIcon color="primary" />}
            />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                    />
                  }
                  label="Email Notifications"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.deadlineReminders}
                      onChange={(e) => setSettings({ ...settings, deadlineReminders: e.target.checked })}
                    />
                  }
                  label="Deadline Reminders"
                />
                <TextField
                  fullWidth
                  label="Reminder Days Before Deadline"
                  type="number"
                  value={settings.reminderDays}
                  onChange={(e) => setSettings({ ...settings, reminderDays: parseInt(e.target.value) })}
                  disabled={!settings.deadlineReminders}
                  inputProps={{ min: 1, max: 30 }}
                />
                
                <Box sx={{ pt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveSettings}
                  >
                    Save Settings
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Bank Holidays */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader 
              title="Bank Holidays"
              avatar={<CalendarIcon color="primary" />}
              action={
                <Stack direction="row" spacing={1}>
                  <IconButton onClick={fetchHolidays}>
                    <RefreshIcon />
                  </IconButton>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setHolidayDialog(true)}
                  >
                    Add Holiday
                  </Button>
                </Stack>
              }
            />
            <Divider />
            <CardContent sx={{ maxHeight: 400, overflow: 'auto' }}>
              {Object.entries(holidaysByYear)
                .sort(([a], [b]) => parseInt(b) - parseInt(a))
                .map(([year, yearHolidays]) => (
                  <Box key={year} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {year} ({yearHolidays.length} holidays)
                    </Typography>
                    <List dense>
                      {yearHolidays.map((holiday) => (
                        <ListItem key={holiday.id} divider>
                          <ListItemText
                            primary={holiday.name}
                            secondary={formatDate(holiday.date)}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleDeleteHoliday(holiday.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              
              {holidays.length === 0 && (
                <Typography color="text.secondary" textAlign="center">
                  No holidays configured
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Projects */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader 
              title="Projects"
              avatar={<ScheduleIcon color="primary" />}
              action={
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setProjectDialog(true)}
                >
                  Add Project
                </Button>
              }
            />
            <Divider />
            <CardContent sx={{ maxHeight: 400, overflow: 'auto' }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((project) => (
                      <TableRow key={project.id}>
                        <TableCell>
                          <Chip label={project.code} size="small" />
                        </TableCell>
                        <TableCell>{project.name}</TableCell>
                        <TableCell>{formatDate(project.created_at)}</TableCell>
                      </TableRow>
                    ))}
                    {projects.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} sx={{ textAlign: 'center' }}>
                          No projects configured
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* System Info */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="System Information" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">API Version</Typography>
                  <Typography variant="body1">1.0.0</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Database</Typography>
                  <Typography variant="body1">PostgreSQL</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Environment</Typography>
                  <Typography variant="body1">Development</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Holiday Dialog */}
      <Dialog open={holidayDialog} onClose={() => setHolidayDialog(false)}>
        <DialogTitle>Add Bank Holiday</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={newHoliday.date}
              onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Holiday Name"
              value={newHoliday.name}
              onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Country</InputLabel>
              <Select
                value={newHoliday.country}
                label="Country"
                onChange={(e) => setNewHoliday({ ...newHoliday, country: e.target.value })}
              >
                <MenuItem value="UK">United Kingdom</MenuItem>
                <MenuItem value="England">England</MenuItem>
                <MenuItem value="Scotland">Scotland</MenuItem>
                <MenuItem value="Wales">Wales</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHolidayDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddHoliday}
            disabled={!newHoliday.date || !newHoliday.name}
          >
            Add Holiday
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Project Dialog */}
      <Dialog open={projectDialog} onClose={() => setProjectDialog(false)}>
        <DialogTitle>Add Project</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              label="Project Code"
              value={newProject.code}
              onChange={(e) => setNewProject({ ...newProject, code: e.target.value.toUpperCase() })}
              helperText="Short code (e.g., IGET, HS2)"
            />
            <TextField
              fullWidth
              label="Project Name"
              value={newProject.name}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Description"
              value={newProject.description}
              onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddProject}
            disabled={!newProject.code || !newProject.name}
          >
            Add Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
